import { type NiceModalHandler, useModal } from '@ebay/nice-modal-react'
import { Link } from '@tanstack/router'
import axios from 'axios'
import dayjs from 'dayjs'
import { type ChangeEventHandler, useEffect, useRef, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import avatar_3 from '../../assets/common/avatar_3.webp'
import guest_avatar from '../../assets/common/guest_avatar.webp'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import closeModal from '../../utils/closeModal'
import ChooseAvatar from '../common/choose-avatar'
import FreeSpins from '../common/free-spins'
import LoginBonusBtn from '../common/login-bonus-btn'
import ToastMessage from '../common/toast-message'
import UploadProfileModal from '../common/upload-profile-modal'
import Icon from '../icon'

type AvatarProps = {
  closeProfileDrawer: () => void
}

const Avatar = ({ closeProfileDrawer }: AvatarProps) => {
  const profile_picture = useBoundStore(state => state.user.profile_picture)
  const regTime = useBoundStore(state => state.user.regTime)
  const setProfilePicture = useBoundStore(state => state.user.setProfilePicture)
  const username = useBoundStore(state => state.user.username)
  // const nickName = useBoundStore(state => state.user.nickName)
  // const registerMethod = useBoundStore(state => state.user.register_method)
  const toastMessage = useModal(ToastMessage)
  const { t } = useTranslation(['drawer', 'personalData'])
  const uploadProfileModal = useModal(UploadProfileModal)
  const fileRef = useRef<HTMLInputElement>(null)
  const [isUploadVisible, setIsUploadVisible] = useState(false)
  const [isChooseAvatarVisible, setIsChooseAvatarVisible] = useState(false)
  const [daysWith, setDaysWith] = useState('')
  const [profileError, setProfileError] = useState(false)

  useEffect(() => {
    const a = dayjs(regTime).format('MM/DD/YYYY')
    const b = dayjs()
    const diffDays = b.diff(a, 'days').toString()
    setDaysWith(diffDays)
  }, [regTime])

  useEffect(() => {
    setProfileError(false)
  }, [profile_picture])

  const onSelectFile: ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.files && e.target.files.length > 0) {
      if (Math.round(e.target.files[0].size / 1024) >= 20480) {
        toastMessage.show({
          text: t('file-too-big', { ns: 'personalData' }),
          isValid: false,
        })
        setTimeout(() => closeModal(toastMessage as NiceModalHandler), 2000)
      } else {
        const fileReader = new FileReader()
        fileReader.onloadend = () => {
          setIsChooseAvatarVisible(false)
          setIsUploadVisible(true)
          uploadProfileModal.show({
            imageSrc: fileReader.result,
          })
        }
        fileReader.readAsDataURL(e.target.files[0])
      }
    }
  }

  useEffect(() => {
    if (!isUploadVisible && fileRef.current) {
      fileRef.current.value = ''
    }
  }, [isUploadVisible, fileRef.current])

  useEffect(() => {
    if (!username) return
    const controller = new AbortController()
    const signal = controller.signal

    const fetchProfileImage = async () => {
      try {
        const response = await api.post<ApiResponse<string>>(
          '/ajax_data.php',
          {
            type: 'get_imgurl',
          },
          { signal },
        )

        const { status, info } = response.data
        if (status === 1) {
          setProfilePicture(info)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchProfileImage()

    return () => {
      controller.abort()
    }
  }, [username])

  const renderContent = () => {
    if (!username)
      return (
        <>
          <div className="overflow-hidden flex relative bg-white mb-2.5 rounded-[50%] border-[3px] border-solid border-white">
            <img
              className="w-[84px] h-[84px] object-cover"
              src={guest_avatar}
              alt="Avatar"
            />
          </div>
          <span className="flx-ctr text-xs font-Syncopate-Bold text-txt uppercase max-w-full text-center relative mb-1 text-[#888] dark:text-[#e9e9ea]">
            {t('welcome-guest', { ns: 'drawer' })}
          </span>
        </>
      )

    return (
      <>
        <div
          className="overflow-hidden flex relative bg-white mb-2.5 rounded-[50%] border-[3px] border-solid border-white"
          onClick={() =>
            setIsChooseAvatarVisible(prev => (prev ? false : true))
          }>
          <img
            className="w-[84px] h-[84px] object-cover"
            src={profile_picture && !profileError ? profile_picture : avatar_3}
            alt="Avatar"
            onError={() => setProfileError(true)}
          />
        </div>
        <Link to="/profile">
          <span
            className="flx-ctr text-xs font-Syncopate-Bold text-txt uppercase max-w-full text-center relative mb-1"
            onClick={closeProfileDrawer}>
            {username}
            <Icon
              className="absolute right-[-13px] -translate-y-2/4 fill-txt top-2/4 ml-[5px] -mt-[1px]"
              id="chevron-right"
              width="7"
            />
          </span>
        </Link>
        {/* {registerMethod !== 'normal' && (
          <Nickname>
            {t('nickname', { ns: 'personalData' })}: {nickName}
          </Nickname>
        )} */}
        <p className="text-[15px] text-txt mb-[5px]">
          {daysWith} {t('days-with', { ns: 'drawer' })}
        </p>
      </>
    )
  }

  return (
    <div className="v-flx-ctr relative pt-[43px] pb-[5px] px-[5px] border-b-[#f5f6f9] dark:border-b-[#1f223c] border-b border-solid">
      <FreeSpins closeProfileDrawer={closeProfileDrawer} />
      <LoginBonusBtn closeProfileDrawer={closeProfileDrawer} />
      <ChooseAvatar
        fileRef={fileRef}
        onSelectFile={onSelectFile}
        isVisible={isChooseAvatarVisible}
      />
      {renderContent()}
    </div>
  )
}

export default Avatar
